import { Show } from "solid-js";
import { Icon } from "../icon/Icon";
import type { TemplateTag } from "../../types/Template";
import { useTranslation } from "../../state/i18n";
import { isNil } from "lodash-es";
import Text from "../text/Text.tsx";

export type TemplateSelectorProps = {
  title: string;
  description: string | undefined;
  onClick: () => void;
  tags?: TemplateTag[];
};

const TemplateSelector = (props: TemplateSelectorProps) => {
  const { t } = useTranslation();

  return (
    <button
      aria-label={
        !isNil(props.tags)
          ? t(($) => $.TEMPLATES_CREATE_BUTTON_ARIA_LABEL, { name: props.title })
          : t(($) => $.TEMPLATES_SCRATCH_CREATE_ARIA_LABEL)
      }
      onClick={() => props.onClick()}
      class="flex h-auto w-full flex-col gap-y-3 rounded-sm border border-subtle bg-surface-default p-3 text-left transition-all hover:bg-surface-hover focus-visible:outline-default active:bg-surface-pressed"
    >
      <Show
        when={props.tags}
        fallback={
          <div class="flex h-33.5 items-center justify-center rounded-sm bg-surface-extra-subtle">
            <Icon name="plus" size="3xl" type="solid" class="icon-tertiary" />
          </div>
        }
      >
        {(tags) => <TemplateThumbnail tag={tags()[0]} />}
      </Show>
      <div class="min-w-0">
        <Text as="h3" truncate={2} class="text-base-semibold text-primary" text={props.title} />
        <Show when={props.description}>
          {(description) => <Text as="p" truncate={2} class="text-base-regular text-secondary" text={description()} />}
        </Show>
      </div>
    </button>
  );
};

type TemplateThumbnailProps = {
  tag: TemplateTag;
};
const TemplateThumbnail = (props: TemplateThumbnailProps) => (
  <svg
    class="h-33.5 w-full shrink-0 rounded-sm object-cover"
    classList={{
      "fill-accent-subtle-teal": props.tag === "INSTALLATION",
      "fill-accent-subtle-yellow": props.tag === "ENERGY_UTILITIES",
      "fill-accent-subtle-indigo": props.tag === "CONSTRUCTION",
      "fill-accent-subtle-orange": props.tag === "MANUFACTURING",
      "fill-accent-subtle-moss-green": props.tag === "FACILITY",
      "fill-accent-subtle-light-blue": props.tag === "SAFETY",
      "fill-accent-subtle-red": props.tag === "AUTOMOTIVE",
      "fill-accent-subtle-pink": props.tag === "INFRASTRUCTURE",
      "fill-accent-subtle-purple": props.tag === "REAL_ESTATE",
      "fill-accent-subtle-green": props.tag === "LOGISTICS",
      "fill-accent-subtle-brand": props.tag === "OTHER",
      "fill-accent-subtle-grey": props.tag === "CUSTOM",
    }}
    viewBox="0 0 1920 1080"
    preserveAspectRatio="xMidYMid slice"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden={true}
  >
    <g clip-path="url(#clip0_2894_71366)">
      <rect width="1920" height="1080" />
      <g filter="url(#filter0_d_2894_71366)">
        <rect x="537" y="320" width="846" height="1084" rx="24" fill="#F3F4F6" shape-rendering="crispEdges" />
        <rect
          x="512"
          y="295"
          width="896"
          height="1134"
          rx="49"
          stroke="white"
          stroke-width="50"
          shape-rendering="crispEdges"
          fill="#F3F4F6"
        />
        <rect x="617" y="419.734" width="382" height="106" rx="16.3901" fill="white" />
        <rect x="617" y="574.904" width="686.336" height="106.536" rx="16.3901" fill="white" />
        <rect x="617" y="730.611" width="686.336" height="106.536" rx="16.3901" fill="white" />
        <rect x="617" y="886.316" width="686.336" height="106.536" rx="16.3901" fill="white" />
        <rect x="617" y="1042.02" width="686.336" height="106.536" rx="16.3901" fill="white" />
      </g>
    </g>
    <defs>
      <filter
        id="filter0_d_2894_71366"
        x="447"
        y="242"
        width="1026"
        height="1264"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="12" />
        <feGaussianBlur stdDeviation="20" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix type="matrix" values="0 0 0 0 0.819608 0 0 0 0 0.835294 0 0 0 0 0.858824 0 0 0 0.16 0" />
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2894_71366" />
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2894_71366" result="shape" />
      </filter>
      <clipPath id="clip0_2894_71366">
        <rect width="1920" height="1080" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export default TemplateSelector;
